// Q&A Management Component for Admin Dashboard
import React, { useState, useEffect } from 'react';
import './qamanagement.css';
import { API_BASE } from '../services/api.js';
import ParentQAManagement from './parentqamanagement';



const QAManagement = ({ onBack }) => {
  // State for Q&As and form
  const [qas, setQAs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingQA, setEditingQA] = useState(null);
  const [showParentManagement, setShowParentManagement] = useState(false);

  // Hierarchy data
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [topics, setTopics] = useState([]);
  const [subtopics, setSubtopics] = useState([]);
  const [chunks, setChunks] = useState([]);
  const [microChunks, setMicroChunks] = useState([]);
  const [parentQuestions, setParentQuestions] = useState([]);  // ADD THIS LINE
  // Add these new state variables after the existing ones
  const [showExcelUpload, setShowExcelUpload] = useState(false);
  const [excelFile, setExcelFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResults, setUploadResults] = useState(null);
  
  // For Excel upload - hierarchy selection
  const [uploadSubjectId, setUploadSubjectId] = useState('');
  const [uploadChapterId, setUploadChapterId] = useState('');
  const [uploadTopicId, setUploadTopicId] = useState('');
  const [uploadSubtopicId, setUploadSubtopicId] = useState('');
  const [uploadQuestionType, setUploadQuestionType] = useState('');

  // List filter (drives which QAs are shown; list is empty until a topic is chosen)
  const [filterSubjectId, setFilterSubjectId] = useState('');
  const [filterChapterId, setFilterChapterId] = useState('');
  const [filterTopicId, setFilterTopicId] = useState('');
  const [filterChapters, setFilterChapters] = useState([]);
  const [filterTopics, setFilterTopics] = useState([]);



  // Form data
  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    question_type: '',
    one_word_answer: '',
    detailed_explanation: '',
    past_year_reference: '',
    hindi_question: '',
    hindi_answer: '',
    hindi_detailed_explanation: '',
    topic_id: null,
    subtopic_id: null,
    chunk_id: null,
    micro_chunk_id: null,
    
    
    // For hierarchy selection
    selected_subject_id: '',
    selected_chapter_id: '',
    selected_topic_id: '',
    selected_subtopic_id: '',
    selected_chunk_id: '',
    selected_micro_chunk_id: ''
  });

  // Question type options
  const questionTypes = [
    'Competency One-liner',
    'Competency Text',
    'Direct One-liner',
    'Direct Text',
    'True/False',
    'Accounts Numericals',
    'Economics Numericals'
  ];

  const RICH_CONTENT_TYPES = ['Accounts Numericals', 'Economics Numericals'];
  const isRichType = RICH_CONTENT_TYPES.includes(formData.question_type);
  
  
  //const API_BASE = `${API_BASE}`;

  // Load initial data
  useEffect(() => {
    loadSubjects();   // only load subjects for the filter; no QA dump on mount
  }, []);

  const loadSubjects = async () => {
    try {
      const response = await fetch(`${API_BASE}/subjects/`);
      const data = await response.json();
      setSubjects(data);
    } catch (err) {
      console.error('Error loading subjects:', err);
    }
  };

  const loadChapters = async (subjectId) => {
    try {
      const response = await fetch(`${API_BASE}/chapters/?subject_id=${subjectId}`);
      const data = await response.json();
      setChapters(data);
    } catch (err) {
      console.error('Error loading chapters:', err);
    }
  };

  const loadTopics = async (chapterId) => {
    try {
      const response = await fetch(`${API_BASE}/topics/?chapter_id=${chapterId}`);
      const data = await response.json();
      setTopics(data);
    } catch (err) {
      console.error('Error loading topics:', err);
    }
  };

  const loadSubtopics = async (topicId) => {
    try {
      const response = await fetch(`${API_BASE}/subtopics/?topic_id=${topicId}`);
      const data = await response.json();
      setSubtopics(data);
    } catch (err) {
      console.error('Error loading subtopics:', err);
    }
  };

  const loadChunks = async (topicId) => {
    try {
      const response = await fetch(`${API_BASE}/chunks/?topic_id=${topicId}`);
      const data = await response.json();
      setChunks(data);
    } catch (err) {
      console.error('Error loading chunks:', err);
    }
  };

  const loadMicroChunks = async (chunkId) => {
    try {
      const response = await fetch(`${API_BASE}/micro-chunks/?chunk_id=${chunkId}`);
      const data = await response.json();
      setMicroChunks(data);
    } catch (err) {
      console.error('Error loading micro chunks:', err);
    }
  };

  const loadParentQuestions = async (topicId, subtopicId = null) => {
    try {
      const params = new URLSearchParams({ topic_id: topicId });
      if (subtopicId) params.append('subtopic_id', subtopicId);
      
      const response = await fetch(`${API_BASE}/predefined-qa-parent/?${params.toString()}`);
      const data = await response.json();
      setParentQuestions(data);
    } catch (err) {
      console.error('Error loading parent questions:', err);
    }
  };

  const loadQAs = async (topicId) => {
    // Only load when a topic is selected — never dump the whole DB
    if (!topicId) { setQAs([]); return; }
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/predefined-qa/?topic_id=${topicId}`);
      if (!response.ok) throw new Error('Failed to load Q&As');
      const data = await response.json();
      setQAs(data);
    } catch (err) {
      setError(`Error loading Q&As: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubjectChange = async (subjectId) => {
    setFormData(prev => ({
      ...prev,
      selected_subject_id: subjectId,
      selected_chapter_id: '',
      selected_topic_id: '',
      selected_subtopic_id: '',
      selected_chunk_id: ''
    }));

    // Reset dependent dropdowns first
    setChapters([]);
    setTopics([]);
    setSubtopics([]);
    setChunks([]);
    setMicroChunks([]);
    
    if (subjectId) {
      await loadChapters(subjectId);
    }
    
  };

  const handleChapterChange = async (chapterId) => {
    setFormData(prev => ({
      ...prev,
      selected_chapter_id: chapterId,
      selected_topic_id: '',
      selected_subtopic_id: '',
      selected_chunk_id: ''
    }));

    // Reset dependent dropdowns first
    setTopics([]);
    setSubtopics([]);
    setChunks([]);
    setMicroChunks([]);
    
    if (chapterId) {
      await loadTopics(chapterId);
    }
    
  };

  const handleTopicChange = async (topicId) => {
    setFormData(prev => ({
      ...prev,
      selected_topic_id: topicId,
      selected_subtopic_id: '',
      selected_chunk_id: '',
      selected_parent_id: ''  // ADD THIS
    }));

    // Reset dependent dropdowns first
    setSubtopics([]);
    setChunks([]);
    setMicroChunks([]);
    setParentQuestions([]);  // ADD THIS
    
    if (topicId) {
      await loadSubtopics(topicId);
      await loadChunks(topicId);
       await loadParentQuestions(topicId);  // ADD THIS
    }
    };

  const handleSubtopicChange = async (subtopicId) => {
    setFormData(prev => ({
      ...prev,
      selected_subtopic_id: subtopicId,
      selected_chunk_id: ''
    }));
    setMicroChunks([]);
  };

  const handleChunkChange = async (chunkId) => {
    setFormData(prev => ({
      ...prev,
      selected_chunk_id: chunkId
    }));
    
    setMicroChunks([]);
    
    if (chunkId) {
      await loadMicroChunks(chunkId);
    }
  };

  const handleFilterSubject = async (subjectId) => {
    setFilterSubjectId(subjectId);
    setFilterChapterId(''); setFilterTopicId('');
    setFilterChapters([]); setFilterTopics([]);
    setQAs([]);
    if (subjectId) {
      try {
        const res = await fetch(`${API_BASE}/chapters/?subject_id=${subjectId}`);
        setFilterChapters(await res.json());
      } catch (e) { console.error(e); }
    }
  };
  const handleFilterChapter = async (chapterId) => {
    setFilterChapterId(chapterId);
    setFilterTopicId(''); setFilterTopics([]); setQAs([]);
    if (chapterId) {
      try {
        const res = await fetch(`${API_BASE}/topics/?chapter_id=${chapterId}`);
        setFilterTopics(await res.json());
      } catch (e) { console.error(e); }
    }
  };
  const handleFilterTopic = (topicId) => {
    setFilterTopicId(topicId);
    loadQAs(topicId);
  };
  
  const openCreateForm = async () => {
    setEditingQA(null);
    setError(null);
    setSuccess(null);

    // Inherit hierarchy from the active list filter
    if (filterSubjectId) {
      await loadChapters(filterSubjectId);
    }
    if (filterChapterId) {
      await loadTopics(filterChapterId);
    }
    if (filterTopicId) {
      await loadSubtopics(filterTopicId);
      await loadChunks(filterTopicId);
      await loadParentQuestions(filterTopicId);
    }

    // Suggest the next order_index based on QAs already loaded for this topic
    const nextOrderIndex = qas.length > 0
      ? Math.max(...qas.map(q => q.order_index ?? 0)) + 1
      : 0;

    setFormData(prev => ({
      ...prev,
      order_index: nextOrderIndex,
      selected_subject_id: filterSubjectId || '',
      selected_chapter_id: filterChapterId || '',
      selected_topic_id: filterTopicId || '',
      selected_subtopic_id: '',
      selected_chunk_id: '',
      selected_micro_chunk_id: '',
    }));

    setShowCreateForm(true);
    setShowExcelUpload(false);
  };
  
  const prepareLinkData = () => {
    const linkData = {};
    
    switch (formData.link_type) {
      case 'micro_chunk':
        if (formData.selected_chunk_id && microChunks.length > 0) {
          const selectedMicroChunk = microChunks.find(mc => mc.micro_chunk_id.toString() === formData.selected_chunk_id);
          if (selectedMicroChunk) {
            linkData.micro_chunk_id = selectedMicroChunk.micro_chunk_id;
          }
        }
        break;
      case 'chunk':
        if (formData.selected_chunk_id) {
          linkData.chunk_id = parseInt(formData.selected_chunk_id);
        }
        break;
      case 'topic':
        if (formData.selected_topic_id) {
          linkData.topic_id = parseInt(formData.selected_topic_id);
        }
        break;
      case 'subtopic':
        if (formData.selected_subtopic_id) {
          linkData.subtopic_id = parseInt(formData.selected_subtopic_id);
        }
        break;
    }
    
    return linkData;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    const isRich = RICH_CONTENT_TYPES.includes(formData.question_type);

    if (!isRich && (!formData.question.trim() || !formData.answer.trim())) {
      setError('Both question and answer are required');
      return;
    }

    if (!formData.selected_topic_id) {
      setError('Topic is required');
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        question: isRich ? (formData.question.trim() || 'Numerical') : formData.question.trim(),
        answer: isRich ? '-' : formData.answer.trim(),
        question_type: formData.question_type || null,
        one_word_answer: isRich ? null : (formData.one_word_answer || null),
        detailed_explanation: isRich ? null : (formData.detailed_explanation || null),
        past_year_reference: formData.past_year_reference || null,
        hindi_question: isRich ? null : (formData.hindi_question || null),
        hindi_answer: isRich ? null : (formData.hindi_answer || null),
        hindi_detailed_explanation: isRich ? null : (formData.hindi_detailed_explanation || null),
        parent_id: formData.selected_parent_id ? parseInt(formData.selected_parent_id) : null,
        order_index: parseInt(formData.order_index) || 0,
        subject_id: parseInt(formData.selected_subject_id),
        chapter_id: parseInt(formData.selected_chapter_id),
        topic_id: parseInt(formData.selected_topic_id),
        subtopic_id: formData.selected_subtopic_id ? parseInt(formData.selected_subtopic_id) : null,
        chunk_id: formData.selected_chunk_id ? parseInt(formData.selected_chunk_id) : null,
        micro_chunk_id: formData.selected_micro_chunk_id ? parseInt(formData.selected_micro_chunk_id) : null,
        generate_embedding: !isRich,          // no embeddings for born-rich
        has_rich_content: isRich              // mark born-rich
      };

      const url = editingQA 
        ? `${API_BASE}/predefined-qa/${editingQA.qa_id}`
        : `${API_BASE}/predefined-qa/`;
      
      const method = editingQA ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      const saved = await response.json();

      setSuccess(editingQA 
        ? 'Q&A updated successfully!'
        : 'Q&A created successfully!'
      );
      
      // Keep hierarchy on create (fast next entry); full reset on edit
      resetForm(!editingQA);
      
      // Reload Q&As for the current filter topic
      loadQAs(filterTopicId);

      // Born-rich: open the rich-content editor for the new QA in a new tab,
      // carrying the current scope so that tab can create the next question itself
      if (isRich && !editingQA && saved && saved.qa_id) {
        const scope = new URLSearchParams({
          subject_id:     formData.selected_subject_id     || '',
          chapter_id:     formData.selected_chapter_id     || '',
          topic_id:       formData.selected_topic_id       || '',
          subtopic_id:    formData.selected_subtopic_id    || '',
          chunk_id:       formData.selected_chunk_id       || '',
          micro_chunk_id: formData.selected_micro_chunk_id || '',
          question_type:  formData.question_type           || '',
          parent_id:      formData.selected_parent_id      || ''
        }).toString();
        window.open(`/admin/qa-rich-content/${saved.qa_id}?${scope}`, '_blank');
      }
      
    } catch (err) {
      setError(`Error ${editingQA ? 'updating' : 'creating'} Q&A: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

    const handleEdit = async (qa) => {
    setEditingQA(qa);
    setError(null);
    setSuccess(null);

    // Reverse-cascade: load Chapters/Topics/Subtopics/Chunks so the
    // hierarchy dropdowns show this QA's actual linkage instead of empty.
    if (qa.subject_id) {
      await loadChapters(qa.subject_id);
    }
    if (qa.chapter_id) {
      await loadTopics(qa.chapter_id);
    }
    if (qa.topic_id) {
      await loadSubtopics(qa.topic_id);
      await loadChunks(qa.topic_id);
      await loadParentQuestions(qa.topic_id);
    }
    if (qa.chunk_id) {
      await loadMicroChunks(qa.chunk_id);
    }

    setFormData({
      question: qa.question,
      answer: qa.answer,
      question_type: qa.question_type || '',
      one_word_answer: qa.one_word_answer || '',
      detailed_explanation: qa.detailed_explanation || '',
      past_year_reference: qa.past_year_reference || '',
      order_index: qa.order_index ?? 0,
      hindi_question: qa.hindi_question || '',
      hindi_answer: qa.hindi_answer || '',
      hindi_detailed_explanation: qa.hindi_detailed_explanation || '',
      topic_id: qa.topic_id,
      subtopic_id: qa.subtopic_id,
      chunk_id: qa.chunk_id,
      micro_chunk_id: qa.micro_chunk_id,
      generate_embedding: true,
      selected_subject_id: qa.subject_id ? qa.subject_id.toString() : '',
      selected_chapter_id: qa.chapter_id ? qa.chapter_id.toString() : '',
      selected_topic_id: qa.topic_id ? qa.topic_id.toString() : '',
      selected_subtopic_id: qa.subtopic_id ? qa.subtopic_id.toString() : '',
      selected_parent_id: qa.parent_id ? qa.parent_id.toString() : '',
      selected_chunk_id: qa.chunk_id ? qa.chunk_id.toString() : '',
      selected_micro_chunk_id: qa.micro_chunk_id ? qa.micro_chunk_id.toString() : ''
    });

    setShowCreateForm(true);
    setShowExcelUpload(false);
  };

  const handleDelete = async (qa) => {
    if (!window.confirm(`Are you sure you want to delete this Q&A?\n\nQuestion: "${qa.question}"\n\nThis action cannot be undone!`)) {
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_BASE}/predefined-qa/${qa.qa_id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setSuccess('Q&A deleted successfully!');
      loadQAs(filterTopicId);
      
    } catch (err) {
      setError(`Error deleting Q&A: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = (keepHierarchy = false) => {
    setFormData(prev => ({
      // content — always cleared, ready for the next entry
      question: '',
      answer: '',
      one_word_answer: '',
      detailed_explanation: '',
      past_year_reference: '',
      hindi_question: '',
      hindi_answer: '',
      hindi_detailed_explanation: '',
      generate_embedding: true,
      // internal id mirrors — cleared
      topic_id: null,
      subtopic_id: null,
      chunk_id: null,
      micro_chunk_id: null,
      // scope — preserved for rapid repeated entry, cleared on a full reset
      question_type:        keepHierarchy ? prev.question_type        : '',
      selected_subject_id:  keepHierarchy ? prev.selected_subject_id  : '',
      selected_chapter_id:  keepHierarchy ? prev.selected_chapter_id  : '',
      selected_topic_id:    keepHierarchy ? prev.selected_topic_id    : '',
      selected_subtopic_id: keepHierarchy ? prev.selected_subtopic_id : '',
      selected_parent_id:   keepHierarchy ? prev.selected_parent_id   : '',
      // deep links — preserved for rapid entry
      selected_chunk_id:       keepHierarchy ? prev.selected_chunk_id       : '',
      selected_micro_chunk_id: keepHierarchy ? prev.selected_micro_chunk_id : ''
    }));
    setEditingQA(null);
    setShowCreateForm(keepHierarchy);   // keep form open for rapid entry; close on full reset
    setError(null);
    setSuccess(null);
  };

  const generateEmbedding = async (qaId) => {
    try {
      const response = await fetch(`${API_BASE}/predefined-qa/${qaId}/generate-embedding`, {
        method: 'POST'
      });
      
      if (response.ok) {
        setSuccess('Embedding generation started in background');
        setTimeout(() => loadQAs(), 2000); // Reload after 2 seconds
      } else {
        setError('Failed to generate embedding');
      }
    } catch (err) {
      setError('Error generating embedding');
    }
  };
  // Excel Upload Functions
  const handleExcelFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel'
      ];
      
      if (!validTypes.includes(file.type)) {
        setError('Please upload a valid Excel file (.xlsx or .xls)');
        return;
      }
      
      setExcelFile(file);
      setError(null);
    }
  };

  const handleExcelUpload = async () => {
    // Validate required fields
    if (!uploadSubjectId || !uploadChapterId || !uploadTopicId || !uploadQuestionType) {
      setError('Please select Subject, Chapter, Topic, and Question Type');
      return;
    }

    if (!excelFile) {
      setError('Please select an Excel file to upload');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', excelFile);
      
      // Build query parameters instead of FormData for non-file fields
      const params = new URLSearchParams({
        subject_id: uploadSubjectId,
        chapter_id: uploadChapterId,
        topic_id: uploadTopicId,
        question_type: uploadQuestionType
      });
      
      // Add optional subtopic_id if selected
      if (uploadSubtopicId) {
        params.append('subtopic_id', uploadSubtopicId);
      }

      const response = await fetch(`${API_BASE}/predefined-qa/bulk-upload?${params.toString()}`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Upload failed');
      }

      const result = await response.json();
      setUploadResults(result);
      setSuccess(`Successfully uploaded ${result.successful} Q&As. Failed: ${result.failed}`);
      
      // Reset upload form
      setExcelFile(null);
      setUploadProgress(100);
      
      // Reload Q&As list
      loadQAs();
      
    } catch (err) {
      setError(`Upload error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const downloadExcelTemplate = () => {
    // Create sample data with exact column names expected by backend (including new fields)
    const csvContent = `English Question,English Answer,One Word Answer,Detailed Explanation,Past Year Reference,Hindi Question,Hindi Answer,Hindi Detailed Explanation,Parent ID,Order Index,Chunk ID,Micro Chunk ID
What is the capital of France?,The capital of France is Paris.,Paris,Paris has been the capital of France since the late 10th century.,Sample 2023,फ्रांस की राजधानी क्या है?,फ्रांस की राजधानी पेरिस है।,पेरिस 10वीं शताब्दी के अंत से फ्रांस की राजधानी रही है।,,1,,
What is 2+2?,2+2 equals 4.,4,This is basic arithmetic addition.,,2+2 क्या है?,2+2 बराबर 4 है।,यह बुनियादी अंकगणितीय जोड़ है।,,2,,`;

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'predefined_qa_template.csv';
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const resetExcelUploadForm = () => {
    setExcelFile(null);
    setUploadSubjectId('');
    setUploadChapterId('');
    setUploadTopicId('');
    setUploadSubtopicId('');
    setUploadQuestionType('');
    setUploadProgress(0);
    setUploadResults(null);
    setShowExcelUpload(false);
  };

  return (
    <div className="qa-management">
      {/* Show Parent Management if toggled */}
      {showParentManagement ? (
        <ParentQAManagement onBack={() => setShowParentManagement(false)} />
      ) : (
        <>
      {/* Header */}
      <div className="management-header">
        <div className="header-left">
          <button className="back-btn" onClick={onBack}>
            ← Back to Dashboard
          </button>
          <h2>Q&A Management</h2>
        </div>
        <div className="header-actions">
          <button 
            className="toggle-btn"
            onClick={() => {
              // Navigate to parent management
              setShowParentManagement(true);
            }}
            disabled={loading}
          >
            📋 Manage Parent Questions
          </button>
          <button 
            className={`toggle-btn ${showExcelUpload ? 'active' : ''}`}
            onClick={() => setShowExcelUpload(!showExcelUpload)}
            disabled={loading}
          >
            {showExcelUpload ? '📝 Manual Entry' : '📊 Excel Upload'}
          </button>
          <button 
            className="add-btn"
            onClick={() => {
              if (showCreateForm) {
                resetForm();            // currently open → Cancel (full reset)
              } else {
                openCreateForm();       // opening → inherit filter hierarchy
              }
            }}
            disabled={loading}
          >
            {showCreateForm ? 'Cancel' : '+ Add Q&A'}
          </button>
        </div>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="qa-banner qa-banner-success">
          <span>✅ {success}</span>
          <button className="qa-banner-close" onClick={() => setSuccess(null)} aria-label="Dismiss">×</button>
        </div>
      )}

      {error && (
        <div className="qa-banner qa-banner-error">
          <span>❌ {error}</span>
          <button className="qa-banner-close" onClick={() => setError(null)} aria-label="Dismiss">×</button>
        </div>
      )}

      {/* Excel Bulk Upload Section */}
      {showExcelUpload && (
        <div className="excel-upload-container">
          <div className="form-card">
            <h3>📊 Bulk Upload Q&As via Excel</h3>
            <p className="upload-description">
              Upload multiple Q&As at once using an Excel file. All Q&As in the file will be linked to the selected hierarchy.
            </p>

            <div className="form-section">
              <h4>Step 1: Download Template</h4>
              <button 
                className="download-template-btn"
                onClick={downloadExcelTemplate}
                type="button"
              >
                📥 Download Excel Template
              </button>
              <p className="help-text">
                Download the template, fill in your Q&As, and upload below.
              </p>
            </div>

            <div className="form-section">
              <h4>Step 2: Select Hierarchy & Question Type</h4>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Subject *</label>
                  <select
                    value={uploadSubjectId}
                    onChange={(e) => {
                      setUploadSubjectId(e.target.value);
                      setUploadChapterId('');
                      setUploadTopicId('');
                      setUploadSubtopicId('');
                      if (e.target.value) loadChapters(e.target.value);
                    }}
                    required
                  >
                    <option value="">Choose Subject...</option>
                    {subjects.map(subject => (
                      <option key={subject.subject_id} value={subject.subject_id}>
                        {subject.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Chapter *</label>
                  <select
                    value={uploadChapterId}
                    onChange={(e) => {
                      setUploadChapterId(e.target.value);
                      setUploadTopicId('');
                      setUploadSubtopicId('');
                      if (e.target.value) loadTopics(e.target.value);
                    }}
                    required
                    disabled={!uploadSubjectId}
                  >
                    <option value="">Choose Chapter...</option>
                    {chapters.map(chapter => (
                      <option key={chapter.chapter_id} value={chapter.chapter_id}>
                        {chapter.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Topic *</label>
                  <select
                    value={uploadTopicId}
                    onChange={(e) => {
                      setUploadTopicId(e.target.value);
                      setUploadSubtopicId('');
                      if (e.target.value) loadSubtopics(e.target.value);
                    }}
                    required
                    disabled={!uploadChapterId}
                  >
                    <option value="">Choose Topic...</option>
                    {topics.map(topic => (
                      <option key={topic.topic_id} value={topic.topic_id}>
                        {topic.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Subtopic (Optional)</label>
                  <select
                    value={uploadSubtopicId}
                    onChange={(e) => setUploadSubtopicId(e.target.value)}
                    disabled={!uploadTopicId}
                  >
                    <option value="">Choose Subtopic (Optional)...</option>
                    {subtopics.map(subtopic => (
                      <option key={subtopic.subtopic_id} value={subtopic.subtopic_id}>
                        {subtopic.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Question Type *</label>
                <select
                  value={uploadQuestionType}
                  onChange={(e) => setUploadQuestionType(e.target.value)}
                  required
                >
                  <option value="">Choose Question Type...</option>
                  {questionTypes.map(type => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-section">
              <h4>Step 3: Upload Excel File</h4>
              
              <div className="file-upload-area">
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleExcelFileChange}
                  id="excel-file-input"
                  style={{ display: 'none' }}
                />
                <label htmlFor="excel-file-input" className="file-upload-label">
                  📎 {excelFile ? excelFile.name : 'Choose Excel File'}
                </label>
                {excelFile && (
                  <button
                    className="clear-file-btn"
                    onClick={() => setExcelFile(null)}
                    type="button"
                  >
                    ✕
                  </button>
                )}
              </div>

              {uploadProgress > 0 && (
                <div className="upload-progress">
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                  <span>{uploadProgress}% Complete</span>
                </div>
              )}

              {uploadResults && (
                <div className="upload-results">
                  <h5>Upload Results:</h5>
                  <p>✅ Successful: {uploadResults.successful}</p>
                  <p>❌ Failed: {uploadResults.failed}</p>
                  {uploadResults.errors && uploadResults.errors.length > 0 && (
                    <div className="upload-errors">
                      <h6>Errors:</h6>
                      <ul>
                        {uploadResults.errors.map((err, idx) => (
                          <li key={idx}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="form-actions">
              <button 
                type="button"
                className="submit-btn" 
                onClick={handleExcelUpload}
                disabled={loading || !excelFile || !uploadSubjectId || !uploadChapterId || !uploadTopicId || !uploadQuestionType}
              >
                {loading ? '⏳ Uploading...' : '📤 Upload Excel'}
              </button>
              <button 
                type="button"
                className="cancel-btn" 
                onClick={resetExcelUploadForm}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
      
      
      {/* Create/Edit Form */}
      {showCreateForm && (
        <div className="qa-form-container">
          <div className="form-card">
            <h3>{editingQA ? 'Edit Q&A' : 'Create New Q&A'}</h3>
            
            <form onSubmit={handleSubmit} className="qa-form">
              {/* Link to Content */}
              <div className="form-section">
                <h4>Link to Hierarchy *</h4>

                <div className="form-row-3">
                <div className="form-group">
                  <label>Select Subject *</label>
                  <select
                    name="selected_subject_id"
                    value={formData.selected_subject_id}
                    onChange={(e) => handleSubjectChange(e.target.value)}
                    required
                  >
                    <option value="">Choose Subject...</option>
                    {subjects.map(subject => (
                      <option key={subject.subject_id} value={subject.subject_id}>
                        {subject.name}
                      </option>
                    ))}
                  </select>
                </div>

                {formData.selected_subject_id && (
                  <div className="form-group">
                    <label>Select Chapter *</label>
                    <select
                      name="selected_chapter_id"
                      value={formData.selected_chapter_id}
                      onChange={(e) => handleChapterChange(e.target.value)}
                      required
                    >
                      <option value="">Choose Chapter...</option>
                      {chapters.map(chapter => (
                        <option key={chapter.chapter_id} value={chapter.chapter_id}>
                          {chapter.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {formData.selected_chapter_id && (
                  <div className="form-group">
                    <label>Select Topic *</label>
                    <select
                      name="selected_topic_id"
                      value={formData.selected_topic_id}
                      onChange={(e) => handleTopicChange(e.target.value)}
                      required
                    >
                      <option value="">Choose Topic...</option>
                      {topics.map(topic => (
                        <option key={topic.topic_id} value={topic.topic_id}>
                          {topic.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                </div>{/* end form-row-3 */}

                {formData.selected_topic_id && (
                  <div className="form-row">
                    <div className="form-group">
                      <label>Select Subtopic (Optional)</label>
                      <select
                        name="selected_subtopic_id"
                        value={formData.selected_subtopic_id}
                        onChange={(e) => handleSubtopicChange(e.target.value)}
                      >
                        <option value="">Choose Subtopic (Optional)...</option>
                        {subtopics.map(subtopic => (
                          <option key={subtopic.subtopic_id} value={subtopic.subtopic_id}>
                            {subtopic.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Link to Chunk (Optional)</label>
                      <select
                        name="selected_chunk_id"
                        value={formData.selected_chunk_id}
                        onChange={(e) => handleChunkChange(e.target.value)}
                      >
                        <option value="">Choose Chunk (Optional)...</option>
                        {chunks.map(chunk => (
                          <option key={chunk.chunk_id} value={chunk.chunk_id}>
                            Chunk {chunk.chunk_id} - {chunk.chunk_text?.substring(0, 50)}...
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {formData.selected_chunk_id && (
                  <div className="form-group">
                    <label>Link to Micro Chunk (Optional)</label>
                    <select
                      name="selected_micro_chunk_id"
                      value={formData.selected_micro_chunk_id}
                      onChange={handleInputChange}
                    >
                      <option value="">Choose Micro Chunk (Optional)...</option>
                      {microChunks.map(microChunk => (
                        <option key={microChunk.micro_chunk_id} value={microChunk.micro_chunk_id}>
                          Micro Chunk {microChunk.micro_chunk_id} - {microChunk.content?.substring(0, 50)}...
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {/* Basic Q&A Fields */}
              <div className="form-section">
                <h4>Question & Answer (English)</h4>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>Question Type</label>
                    <select
                      name="question_type"
                      value={formData.question_type || ''}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Question Type...</option>
                      {Array.from(new Set([...questionTypes, ...RICH_CONTENT_TYPES])).map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>{isRichType ? 'Question (optional note — full question is authored as blocks)' : 'Question *'}</label>
                    <textarea
                      name="question"
                      value={formData.question}
                      onChange={handleInputChange}
                      placeholder={isRichType ? 'Optional — leave blank; a label is auto-generated' : 'Enter the question...'}
                      rows="3"
                      required={!isRichType}
                    />
                  </div>
                </div>

                {/* Parent Question — moved here for natural order */}
                {formData.selected_topic_id &&
                (formData.question_type === 'Competency One-liner' || formData.question_type === 'Direct One-liner') && (
                  <div className="form-group">
                    <label>Parent Question (Optional - for one-liner questions)</label>
                    <select
                      name="selected_parent_id"
                      value={formData.selected_parent_id || ''}
                      onChange={handleInputChange}
                    >
                      <option value="">No Parent (Standalone Question)</option>
                      {parentQuestions.map(parent => (
                        <option key={parent.parent_id} value={parent.parent_id}>
                          Parent #{parent.parent_id}: {parent.question_stem.substring(0, 60)}...
                        </option>
                      ))}
                    </select>
                    <p className="help-text">
                      Select a parent question if this is a child question (like a, b, c options)
                    </p>
                  </div>
                )}

                {!isRichType && (
                <div className="form-group">
                  <label>Answer *</label>
                  <textarea
                    name="answer"
                    value={formData.answer}
                    onChange={handleInputChange}
                    placeholder="Enter the answer..."
                    rows="4"
                    required
                  />
                </div>
                )}

                <div className="form-row">
                  {!isRichType && (
                  <div className="form-group">
                    <label>One Word Answer</label>
                    <input
                      type="text"
                      name="one_word_answer"
                      value={formData.one_word_answer || ''}
                      onChange={handleInputChange}
                      placeholder="For one-liner questions..."
                    />
                  </div>
                  )}

                    <div className="form-group">
                      <label>Past Year Reference</label>
                      <input
                        type="text"
                        name="past_year_reference"
                        value={formData.past_year_reference || ''}
                        onChange={handleInputChange}
                        placeholder="e.g., JEE 2023, NEET 2022"
                      />
                    </div>
                    <div className="form-group">
                      <label>Order Index</label>
                      <input
                        type="number"
                        name="order_index"
                        value={formData.order_index ?? 0}
                        onChange={handleInputChange}
                        placeholder="0"
                      />
                    </div>
                  </div>

                {!isRichType && (
                <div className="form-group">
                  <label>Detailed Explanation</label>
                  <textarea
                    name="detailed_explanation"
                    value={formData.detailed_explanation || ''}
                    onChange={handleInputChange}
                    placeholder="Detailed explanation of the answer..."
                    rows="3"
                  />
                </div>
                )}
              </div>

              {/* Hindi Content Section — hidden for rich types (Hindi authored as blocks) */}
              {!isRichType && (
              <div className="form-section">
                <details className="qa-fold">
                  <summary>Hindi Content (optional)</summary>
                  <div className="qa-fold-body">
                    <div className="form-group">
                      <label>Hindi Question</label>
                      <textarea
                        name="hindi_question"
                        value={formData.hindi_question || ''}
                        onChange={handleInputChange}
                        placeholder="हिंदी में प्रश्न..."
                        rows="3"
                      />
                    </div>

                    <div className="form-group">
                      <label>Hindi Answer</label>
                      <textarea
                        name="hindi_answer"
                        value={formData.hindi_answer || ''}
                        onChange={handleInputChange}
                        placeholder="हिंदी में उत्तर..."
                        rows="4"
                      />
                    </div>

                    <div className="form-group">
                      <label>Hindi Detailed Explanation</label>
                      <textarea
                        name="hindi_detailed_explanation"
                        value={formData.hindi_detailed_explanation || ''}
                        onChange={handleInputChange}
                        placeholder="विस्तृत व्याख्या..."
                        rows="3"
                      />
                    </div>
                  </div>
                </details>
              </div>
              )}

              
              {/* Form Actions */}
              <div className="form-actions">
                <button type="submit" className="submit-btn" disabled={loading}>
                  {loading ? 'Processing...' : editingQA ? 'Update Q&A' : 'Create Q&A'}
                </button>
                <button type="button" className="cancel-btn" onClick={() => resetForm()}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      
      
      {/* Q&A List — hidden while the Add/Edit form or Excel upload is open */}
      <div className="qa-list-container" style={{ display: (showCreateForm || showExcelUpload) ? 'none' : 'block' }}>
        <div className="qa-filters">
          <span className="qa-filter-label">Filter:</span>
          <select className="qa-filter-select" value={filterSubjectId}
                  onChange={(e) => handleFilterSubject(e.target.value)}>
            <option value="">Subject…</option>
            {subjects.map(s => <option key={s.subject_id} value={s.subject_id}>{s.name}</option>)}
          </select>
          <select className="qa-filter-select" value={filterChapterId}
                  onChange={(e) => handleFilterChapter(e.target.value)} disabled={!filterSubjectId}>
            <option value="">Chapter…</option>
            {filterChapters.map(c => <option key={c.chapter_id} value={c.chapter_id}>{c.name}</option>)}
          </select>
          <select className="qa-filter-select" value={filterTopicId}
                  onChange={(e) => handleFilterTopic(e.target.value)} disabled={!filterChapterId}>
            <option value="">Topic…</option>
            {filterTopics.map(t => <option key={t.topic_id} value={t.topic_id}>{t.name}</option>)}
          </select>
        </div>

        {!filterTopicId ? (
          <div className="qa-list-prompt">Select a subject, chapter, and topic to view its questions.</div>
        ) : (
          <h3>Questions in this topic ({qas.length})</h3>
        )}
        
        {loading && !showCreateForm && (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading Q&As...</p>
          </div>
        )}

        {!loading && qas.length === 0 && (
          <div className="empty-state">
            <p>
              {(filterSubjectId || filterChapterId || filterTopicId)
                ? 'No Q&As match this filter.'
                : 'No Q&As found. Create your first Q&A!'}
            </p>
          </div>
        )}

        {!loading && qas.length > 0 && (
          <div className="qa-grid">
            {qas.map(qa => (
              <div key={qa.qa_id} className="qa-card">
                <div className="qa-card-header">
                  <div className="qa-meta">
                    <span className="qa-id">ID: {qa.qa_id}</span>
                    {qa.question_type && (
                      <span className="question-type-badge">
                        {qa.question_type}
                      </span>
                    )}
                    <span className={`embedding-badge ${qa.has_embedding ? 'yes' : 'no'}`}>
                      {qa.has_embedding ? '🔍 EN Embedded' : '⏳ No EN Embedding'}
                    </span>
                    {qa.hindi_question && (
                      <span className={`embedding-badge ${qa.has_hindi_embedding ? 'yes' : 'no'}`}>
                        {qa.has_hindi_embedding ? '🔍 HI Embedded' : '⏳ No HI Embedding'}
                      </span>
                    )}
                  </div>
                  <div className="qa-actions">
                    <button 
                      className="action-btn edit"
                      onClick={() => handleEdit(qa)}
                      title="Edit Q&A"
                    >
                      ✏️
                    </button>
                    <button 
                      className="action-btn"
                      onClick={() => window.open(`/admin/qa-rich-content/${qa.qa_id}`, '_blank')}
                      title="Add / edit rich content (question & solution blocks)"
                    >
                      📄
                    </button>
                    {(!qa.has_embedding || (qa.hindi_question && !qa.has_hindi_embedding)) && (
                      <button 
                        className="action-btn embed"
                        onClick={() => generateEmbedding(qa.qa_id)}
                        title="Generate Embedding"
                      >
                        🔍
                      </button>
                    )}
                    <button 
                      className="action-btn delete"
                      onClick={() => handleDelete(qa)}
                      title="Delete Q&A"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
                
                <div className="qa-content">
                  <div className="qa-question">
                    <strong>Q (EN):</strong> {qa.question}
                  </div>
                  <div className="qa-answer">
                    <strong>A (EN):</strong> {qa.answer}
                  </div>
                  
                  {qa.one_word_answer && (
                    <div className="qa-one-word">
                      <strong>One Word:</strong> {qa.one_word_answer}
                    </div>
                  )}
                  
                  {qa.detailed_explanation && (
                    <div className="qa-explanation">
                      <strong>Explanation:</strong> {qa.detailed_explanation.substring(0, 100)}
                      {qa.detailed_explanation.length > 100 && '...'}
                    </div>
                  )}
                  
                  {qa.past_year_reference && (
                    <div className="qa-reference">
                      <strong>Reference:</strong> {qa.past_year_reference}
                    </div>
                  )}
                  
                  {qa.hindi_question && (
                    <div className="qa-hindi-section">
                      <div className="qa-question">
                        <strong>Q (HI):</strong> {qa.hindi_question}
                      </div>
                      {qa.hindi_answer && (
                        <div className="qa-answer">
                          <strong>A (HI):</strong> {qa.hindi_answer}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Link Info */}
                  <div className="qa-link-info">
                    <strong>Linked to:</strong> Topic {qa.topic_id}
                    {qa.subtopic_id && ` → Subtopic ${qa.subtopic_id}`}
                    {qa.chunk_id && ` → Chunk ${qa.chunk_id}`}
                    {qa.micro_chunk_id && ` → Micro Chunk ${qa.micro_chunk_id}`}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
        </>
      )}
    </div>
  );
};

export default QAManagement;